import { Component, HostListener } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { routeTransitionAnimations } from './route-transition-animations';
import { LocaleService } from '@shared/services/locale.service';
import { TitleSyncService } from '@core/services/title-sync.service';
import { LaunchDarklyService } from '@core/services/launchdarkly.service';
import { ThemeService } from '@shared/services/theme.service';
import { LDTheme } from '@core/services/launchdarkly.service';
import { RouteAnimationsService } from '@shared/services/route-animations.service';
import { BrowserTelemetryService } from '@core/services/browser-telemetry.service';
import { Subscription } from 'rxjs';
import { VIDEO_TARGET } from '@shared/components/video-overlay/video-overlay.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeTransitionAnimations],
  standalone: false,
})
export class AppComponent {
  title = 'fresh-ai-webapp';
  isOrderConversationPage = false;

  private previousThemeClass?: string;
  private subs = new Subscription();
  public videoTarget = VIDEO_TARGET.Global;

  constructor(
    private router: Router,
    private localeService: LocaleService,
    private launchDarklyService: LaunchDarklyService,
    private themeService: ThemeService,
    private routeAnimationsService: RouteAnimationsService,
    private browserTelemetryService: BrowserTelemetryService,
    _titleSync: TitleSyncService,
  ) {
    this.nukeServiceWorkersOnce();

    this.launchDarklyService.getCampaignTypes().then(([_s, _r, _c, locationData]) => {
      const campaignString = this.launchDarklyService.getCampaignString();
      const daypart = locationData.menu.toLowerCase();
      this.subs.add(this.themeService.theme$(campaignString, daypart).subscribe((theme) => this.applyTheme(theme)));
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isOrderConversationPage = event.url.startsWith('/order-conversation');
      }
    });
  }

  //Listenings for Back and Forward button presses. Triggers a reload of all components in application.
  @HostListener('window:popstate')
  onPopState() {
    location.reload();
  }

  prepareRoute(outlet: RouterOutlet) {
    if (!this.routeAnimationsService.getShouldAnimate()) {
      return undefined;
    }
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animationState'];
  }

  private applyTheme(theme: LDTheme | undefined): void {
    if (this.previousThemeClass) {
      document.body.classList.remove(this.previousThemeClass);
    }

    if (theme?.class) {
      document.body.classList.add(theme.class);
      this.previousThemeClass = theme.class;
    } else {
      this.previousThemeClass = undefined;
    }
  }

  // Temporary to back out the use of SW. Remove this if/when ServiceWorkers are re-introduced.
  private async nukeServiceWorkersOnce() {
    if (!('serviceWorker' in navigator)) return;
    // avoid running on every reload
    if (localStorage.getItem('sw-cleaned') === '1') return;

    try {
      // Unregister all service workers for this origin
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));

      // Clear caches (Angular uses "ngsw:" prefixes)
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));

      localStorage.setItem('sw-cleaned', '1');
      // Detach any still-controlled tabs
      location.reload();
    } catch {
      // ignore; optional: add your own logging
    }
  }
}
